/**
 * @module input/inputRouter
 * Enforces strict input routing based on game state.
 *
 * The state machine contract:
 *  - PLAYING           → slash detection active, dwell-click DISABLED
 *  - READY             → NEITHER input (camera/hand readiness gate + countdown);
 *                        the hand cursor stays visible so the player can confirm
 *                        tracking works before fruit starts flying
 *  - All other states  → dwell-click active, slash detection DISABLED
 *
 * This prevents any possible cross-contamination: slashing in a menu or
 * dwell-selecting during gameplay. All input consumers must check the
 * appropriate gate before acting on user input.
 */

import type { GameState } from "../game/StateMachine.js";

/** The set of states where slash detection is permitted. */
const SLASH_STATES: ReadonlySet<GameState> = new Set(["PLAYING"]);

/** The set of states where dwell-click is permitted. */
const DWELL_STATES: ReadonlySet<GameState> = new Set([
  "MENU",
  "MODESELECT",
  "PAUSED",
  "GAMEOVER",
]);

/** States where the fingertip cursor is rendered — dwell states plus READY. */
const CURSOR_STATES: ReadonlySet<GameState> = new Set([
  ...DWELL_STATES,
  "READY",
]);

/**
 * Stateless routing helper — query these before dispatching input events.
 * Pass the current {@link GameState} each call so there is no stale state.
 */
export const InputRouter = {
  /** Returns `true` when slash detection should be processed. */
  slashEnabled(state: GameState): boolean {
    return SLASH_STATES.has(state);
  },

  /** Returns `true` when dwell-click should be processed. */
  dwellEnabled(state: GameState): boolean {
    return DWELL_STATES.has(state);
  },

  /**
   * Returns `true` when the cursor (fingertip pointer) should be rendered.
   * The cursor is visible in all non-PLAYING states (including READY, where
   * the player needs to SEE that hand tracking works before the countdown
   * ends); hidden in PLAYING to avoid visual noise over the gameplay canvas.
   */
  cursorVisible(state: GameState): boolean {
    return CURSOR_STATES.has(state);
  },
} as const;
